import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  FormControlLabel,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import pick from "lodash/pick";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Checkbox, CustomMultiSelect, Input, Select } from "../Shared/mui-formik-inputs";
import { TourSchema } from "constants/validation-schemas";
import { TourFormAllowedFields } from "constants/forms-submit-allowed-fields";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import ColorPicker from "components/Shared/ColorPicker";
import { selectUser } from "redux/slices/userSlice";
import { selectAccountPermission } from "redux/slices/accountPermissionsSlice";
import { selectTours } from "redux/slices/tourSlice";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { sortOrder } from "util/customers/deliveryTimings";
import { getTourDeliveryTimings } from "util/helpers";
import TourRouteVisualization from "./TourRouteVisualization";

const useStyles = makeStyles({
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    fontSize: "35px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
  },
  _edittext: {
    height: "16px",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
  },
  _cancel: {
    color: "#525252",
    font: " normal normal normal 14px/20px Roboto",
    display: "none",
    position: "absolute",
    marginLeft: "-55px",
    transition: "all 0.3s ease-in-out",
  },
  _savetext: {
    color: "#6F9CEB",
    font: " normal normal normal 14px/20px Roboto",
    display: "none",
    position: "absolute",
    marginLeft: "35px",
    transition: "all 0.3s ease-in-out",
  },
  _timingCard: {
    marginBottom: "16px",
    border: "1px solid #e0e0e0",
  },
  _timingCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    backgroundColor: "#f5f5f5",
  },
  _addTimingButton: {
    marginTop: "16px",
    backgroundColor: "transparent",
    borderColor: "#6F9CEB",
    color: "#6F9CEB",
  },
});

const TOUR_COLOR_PRESETS = ["#00C853", "#FF6D00", "#FFAB00", "#42A5F5", "#9C27B0", "#E91E63"];

function randomTourColor() {
  return TOUR_COLOR_PRESETS[Math.floor(Math.random() * TOUR_COLOR_PRESETS.length)];
}

const TourForm = ({ initialValues, onSubmit, action, supplierLocations }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const defaultColorRef = useRef(randomTourColor());
  const history = useHistory();
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const accountPermissions = useSelector(selectAccountPermission);
  const tours = useSelector(selectTours);
  const { id } = useParams();
  const [availableSupplierLocations, setAvailableSupplierLocations] = useState([]);
  const [endLocations, setEndLocations] = useState([]);
  const [beforeEndLocations, setBeforeEndLocations] = useState([]);
  const [pickupRoutesMap, setPickupRoutesMap] = useState({});
  const [pickupStopSelections, setPickupStopSelections] = useState({});
  const [routeConfigs, setRouteConfigs] = useState({});
  const [tourTypeDialog, setTourTypeDialog] = useState({
    open: false,
    message: "",
    nextCategory: null,
    resetAll: false,
    resetVrp: false,
  });
  const mixSyncRef = useRef(false);
  const confirmVrpResetRef = useRef(false);
  const vehicleCountOptions = useMemo(
    () => Array.from({ length: 10 }, (_, index) => ({
      label: String(index + 1),
      value: index + 1,
    })),
    [],
  );

  const validateTourIdUniqueness = (values) => {
    const errors = {};

    if (values.user_defined_tour_id) {
      const existingTour = tours.find(tour =>
        String(tour.user_defined_tour_id) === String(values.user_defined_tour_id) &&
        tour.id !== initialValues?.id
      );

      if (existingTour) {
        errors.user_defined_tour_id = t("Tour ID must be unique.");
      }
    }

    return errors;
  };

  const getPickupRoutesForTimings = (timings) =>
    (timings || []).map((timing) => {
      const config = pickupRoutesMap[timing] || {};
      return {
        delivery_timing: timing,
        pickup_start: config.pickup_start || null,
        pickup_end: config.pickup_end || null,
        stops: Array.isArray(config.stops) ? config.stops : [],
      };
    });

  const normalizePickupStops = (pickupStops) => {
    if (!Array.isArray(pickupStops)) return [];
    return [...pickupStops]
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((stop) => stop?.supplier_location_id)
      .filter(Boolean);
  };

  const getPickupRoutesFromTour = (tour) => {
    let timingConfigs = tour?.delivery_timing_configs;

    if (typeof timingConfigs === "string") {
      try {
        timingConfigs = JSON.parse(timingConfigs);
      } catch (err) {
        timingConfigs = [];
      }
    }

    if (Array.isArray(timingConfigs) && timingConfigs.length > 0) {
      const routes = timingConfigs
        .map((config) => {
          let pickupStops = config?.pickup_stops;
          if (typeof pickupStops === "string") {
            try {
              pickupStops = JSON.parse(pickupStops);
            } catch (err) {
              pickupStops = [];
            }
          }
          return {
            delivery_timing: config.delivery_timing,
            pickup_start: config.pickup_start || null,
            pickup_end: config.pickup_end || null,
            stops: normalizePickupStops(pickupStops),
          };
        })
        .filter((route) => route.pickup_start || route.pickup_end || (route.stops || []).length > 0);

      if (routes.length > 0) {
        return routes;
      }
    }

    let pickupRoutes = tour?.pickup_routes;

    if (typeof pickupRoutes === "string") {
      try {
        pickupRoutes = JSON.parse(pickupRoutes);
      } catch (err) {
        pickupRoutes = [];
      }
    }

    if (Array.isArray(pickupRoutes) && pickupRoutes.length > 0) {
      return pickupRoutes;
    }

    if (tour?.pickup_start || tour?.pickup_end) {
      const timings = getTourDeliveryTimings(tour);
      return timings.map((timing) => ({
        delivery_timing: timing,
        pickup_start: tour.pickup_start || null,
        pickup_end: tour.pickup_end || null,
        stops: Array.isArray(tour.pickup_route_stops) ? tour.pickup_route_stops : [],
      }));
    }

    return [];
  };

  const validatePickupRoutes = (values) => {
    const errors = {};
    const isPickupOrMix = values.tour_type_category === "pickup" || values.tour_type_category === "mix";
    if (!isPickupOrMix) {
      return errors;
    }

    const timings = values.delivery_timings || [];
    const pickupRoutes = getPickupRoutesForTimings(timings);

    pickupRoutes.forEach((route) => {
      const timingKey = route.delivery_timing;
      if (!route.pickup_start) {
        errors[`${timingKey}_pickup_start`] = "Required";
      }
      if (!route.pickup_end) {
        errors[`${timingKey}_pickup_end`] = "Required";
      }
      if (route.pickup_start && route.pickup_end && route.pickup_start === route.pickup_end) {
        errors[`${timingKey}_pickup_end`] = t("Pickup start and end locations must be different.");
      }
      if (
        values.tour_type_category === "mix" &&
        route.pickup_end &&
        (values.custom_route_enabled
          ? values[`${timingKey}_supplier_location_id`]
          : values.supplier_location_id) &&
        route.pickup_end !==
        (values.custom_route_enabled
          ? values[`${timingKey}_supplier_location_id`]
          : values.supplier_location_id)
      ) {
        errors[`${timingKey}_pickup_end`] = t("Pickup End / Delivery Start must match.");
      }
    });

    pickupRoutes.forEach((route) => {
      if (!route.pickup_start || !route.pickup_end || !route.delivery_timing) {
        return;
      }
      const duplicateTour = tours.find((tour) => {
        if (tour?.active === false) {
          return false;
        }
        const tourPickupRoutes = getPickupRoutesFromTour(tour);
        if (tour.id === initialValues?.id || tourPickupRoutes.length === 0) {
          return false;
        }
        return tourPickupRoutes.some((tourRoute) =>
          tourRoute.delivery_timing === route.delivery_timing &&
          tourRoute.pickup_start === route.pickup_start &&
          tourRoute.pickup_end === route.pickup_end
        );
      });

      if (duplicateTour) {
        const startName = supplierLocations.find((o) => o.id === route.pickup_start)?.name || route.pickup_start;
        const endName = supplierLocations.find((o) => o.id === route.pickup_end)?.name || route.pickup_end;
        const departureName = t(route.delivery_timing);
        errors[`${route.delivery_timing}_pickup_end`] = t(
          "A Pickup route from {{start}} to {{end}} already exists for Departure: {{departure}} in Tour: {{tour}}.",
          {
            start: startName,
            end: endName,
            departure: departureName,
            tour: duplicateTour.name || duplicateTour.id,
          }
        );
      }
    });

    return errors;
  };

  const validateRouteConfigs = (values) => {
    const errors = {};
    const isPickupTour = values.tour_type_category === "pickup";

    if (!isPickupTour) {
      if (values.custom_route_enabled) {
        if (Array.isArray(values.delivery_timings)) {
          values.delivery_timings.forEach((timing, index) => {
            const config = routeConfigs[timing] || {};
            const slId = values[`${timing}_supplier_location_id`] || config.supplier_location_id;
            const timingTourType = config.tour_type || values[`${timing}_tour_type`] || "one_way";

            if (!slId) {
              errors[`${timing}_supplier_location_id`] = t("Required");
              errors[`delivery_timing_configs[${index}].supplier_location_id`] = t("Required");
            }

            if (timingTourType === "custom") {
              const endPt = config.tour_type === "return_to_origin" ? slId : (values[`${timing}_end_point`] || config.end_point);
              if (!endPt) {
                errors[`${timing}_end_point`] = t("Required");
                errors[`delivery_timing_configs[${index}].end_point`] = t("Required");
              }
            }
          });
        }
      } else {
        if (!values.supplier_location_id) {
          errors.supplier_location_id = t("Required");
        }
        if (values.tour_type === "custom" && !values.end_point) {
          errors.end_point = t("Required");
        }
      }
    }

    return errors;
  };

  const validateTourForm = (values) => ({
    ...validateTourIdUniqueness(values),
    ...validatePickupRoutes(values),
    ...validateRouteConfigs(values),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: TourSchema,
    validate: validateTourForm,
    initialValues: {
      name: "",
      description: "",
      transport_agent_id: 1,
      active: true,
      is_default: false,
      middle_mile_digital: false,
      third_party: false,
      color: initialValues?.color ?? defaultColorRef.current,
      delivery_timing_configs: [],
      allowed_supplier_locations: [],
      supplier_location_id: initialValues?.supplier_location_id || null,
      user_defined_tour_id: initialValues?.user_defined_tour_id || null,
      tour_type: initialValues?.tour_type || "one_way",
      route_type: initialValues?.route_type || "distances",
      custom_route_enabled: initialValues?.custom_route_enabled || false,
      tour_type_category: initialValues?.tour_type_category || initialValues?.tour_category || "delivery",
      pickup_route_stops: initialValues?.pickup_route_stops || [],
      pickup_routes: initialValues?.pickup_routes || [],
      ...initialValues,
      delivery_timings: getTourDeliveryTimings(initialValues),
    },
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const isPickupTour = values.tour_type_category === "pickup";
        const isMixTour = values.tour_type_category === "mix";
        const hasPickupRoutes = isPickupTour || isMixTour;
        let stops = [];
        let custom_routes = [];

        if (!isPickupTour && values.custom_route_enabled) {
          const updatedTimingConfigs = (values.delivery_timings || []).map(timing => {
            const config = (values.delivery_timing_configs || []).find(c => c.delivery_timing === timing) || { delivery_timing: timing };
            const routeConfig = routeConfigs[timing] || {};

            const fixStops = [];
            if (routeConfig.tour_type !== "one_way" && routeConfig.addStopsBeforeEndPoint && Array.isArray(routeConfig.beforeEndLocations)) {
              routeConfig.beforeEndLocations.forEach((location) => {
                const val = values[`${timing}_before_end_point_${location.id}`];
                if (val) {
                  fixStops.push({
                    type: "before_end",
                    supplier_location_id: val
                  });
                }
              });
            }

            const endPointVal = routeConfig.tour_type === "return_to_origin"
              ? (routeConfig.supplier_location_id || values[`${timing}_supplier_location_id`] || values.supplier_location_id)
              : (routeConfig.end_point || values[`${timing}_end_point`] || values.end_point || null);

            if (routeConfig.tour_type === "custom" && endPointVal) {
              fixStops.push({
                type: "end_point",
                supplier_location_id: endPointVal
              });
            }

            if (routeConfig.tour_type !== "one_way" && routeConfig.addStopsAfterEndPoint && Array.isArray(routeConfig.endLocations)) {
              routeConfig.endLocations.forEach((location) => {
                const val = values[`${timing}_end_point_${location.id}`];
                if (val) {
                  fixStops.push({
                    type: "after_end",
                    supplier_location_id: val
                  });
                }
              });
            }

            const supplierLocationId = routeConfig.supplier_location_id || values[`${timing}_supplier_location_id`] || values.supplier_location_id || null;

            return {
              ...config,
              tour_type: routeConfig.tour_type || values[`${timing}_tour_type`] || values.tour_type || "one_way",
              route_type: routeConfig.route_type || values[`${timing}_route_type`] || values.route_type || "distances",
              supplier_location_id: supplierLocationId,
              route_start: supplierLocationId,
              route_end: endPointVal,
              fix_stops: fixStops.length > 0 ? fixStops : []
            };
          });

          values.delivery_timing_configs = updatedTimingConfigs;
          values.stops = [];
        } else if (!isPickupTour) {
          if (values.tour_type === "custom") {
            if (values.end_point) {
              stops.push({ type: "end_point", supplier_location_id: values.end_point });
            }
            if (addStopsBeforeEndPoint && Array.isArray(beforeEndLocations)) {
              beforeEndLocations.forEach((location) => {
                const val = values[`before_end_point_${location.id}`];
                if (val) {
                  stops.push({ type: "before_end", supplier_location_id: val });
                }
              });
            }
            if (addStopsAfterEndPoint && Array.isArray(endLocations)) {
              endLocations.forEach((location) => {
                const val = values[`end_point_${location.id}`];
                if (val) {
                  stops.push({ type: "after_end", supplier_location_id: val });
                }
              });
            }
          } else if (values.tour_type === "return_to_origin") {
            if (addStopsBeforeEndPoint && Array.isArray(beforeEndLocations)) {
              beforeEndLocations.forEach((location) => {
                const val = values[`before_end_point_${location.id}`];
                if (val) {
                  stops.push({ type: "before_end", supplier_location_id: val });
                }
              });
            }
            if (addStopsAfterEndPoint && Array.isArray(endLocations)) {
              endLocations.forEach((location) => {
                const val = values[`end_point_${location.id}`];
                if (val) {
                  stops.push({ type: "after_end", supplier_location_id: val });
                }
              });
            }
          }
          if (values.tour_type === "one_way") {
            stops = [];
          }

          values.stops = stops;
        } else {
          values.stops = [];
        }

        const pickupRoutes = hasPickupRoutes ? getPickupRoutesForTimings(values.delivery_timings) : [];
        const pickupRoutesByTiming = pickupRoutes.reduce((acc, route) => {
          acc[route.delivery_timing] = route;
          return acc;
        }, {});
        const customValidationErrors = {
          ...validateTourIdUniqueness(values),
          ...validatePickupRoutes(values),
          ...validateRouteConfigs(values),
        };
        if (Object.keys(customValidationErrors).length > 0) {
          setErrors(customValidationErrors);
          setSubmitting(false);
          return;
        }
        try {
          await TourSchema.validate(values, { abortEarly: false });
        } catch (validationError) {
          if (validationError.inner) {
            const formikErrors = {};
            validationError.inner.forEach((err) => {
              if (err.path) formikErrors[err.path] = err.message;
            });
            setErrors(formikErrors);
          }
          setSubmitting(false);
          return;
        }

        const submitValues = pick({ ...values }, TourFormAllowedFields);

        if (confirmVrpResetRef.current) {
          submitValues.confirm_vrp_reset = true;
          confirmVrpResetRef.current = false;
        }

        if (submitValues.delivery_timing_configs) {
          submitValues.delivery_timing_configs = submitValues.delivery_timing_configs.map(config => {
            const pickupRoute = pickupRoutesByTiming[config.delivery_timing];
            const isScanningEnabled = Boolean(config.scanning_codes_enabled);
            return {
              delivery_timing: config.delivery_timing,
              scanning_codes_enabled: isScanningEnabled,
              allow_manual_orders_marking: isScanningEnabled ? Boolean(config.allow_manual_orders_marking) : false,
              bypass_route_start_verification: isScanningEnabled ? Boolean(config.bypass_route_start_verification) : false,
              num_vehicles: !isPickupTour && !isMixTour ? (config.num_vehicles ?? 1) : 1,
              ...(!isPickupTour && values.custom_route_enabled && {
                tour_type: config.tour_type,
                route_type: config.route_type,
                supplier_location_id: config.supplier_location_id,
                route_start: config.route_start,
                route_end: config.route_end,
                fix_stops: config.fix_stops
              }),
              ...(hasPickupRoutes && {
                pickup_start: pickupRoute?.pickup_start || null,
                pickup_end: pickupRoute?.pickup_end || null,
                pickup_stops: (pickupRoute?.stops || []).map((id, index) => ({
                  supplier_location_id: id,
                  order: index + 1,
                })),
              }),
            };
          });
        }

        if (hasPickupRoutes && isPickupTour) {
          submitValues.custom_route_enabled = false;
          submitValues.stops = [];
          submitValues.is_default = false;
          submitValues.middle_mile_digital = false;
          submitValues.third_party = false;
        }

        if (submitValues.tour_type_category !== "delivery") {
          submitValues.middle_mile_digital = false;
        }

        if (isPickupTour) {
          delete submitValues.route_type;
          delete submitValues.tour_type;
        } else if (values.custom_route_enabled) {
          submitValues.tour_type = "one_way";
          submitValues.stops = [];
        }

        if (!isPickupTour && values.custom_route_enabled && Array.isArray(values.delivery_timings)) {
          submitValues.custom_routes = values.delivery_timings.map((timing) => {
            const field = (base) => `${base}_${timing}`;
            const timingTourType = values[field("tour_type")] || values.tour_type;
            const timingRouteType = values[field("route_type")] || values.route_type;
            const supplierLocationId = values[field("supplier_location_id")] || values.supplier_location_id;
            const endPoint = values[field("end_point")] || values.end_point;

            let stops = [];
            if (timingTourType === "custom") {
              if (endPoint) stops.push({ type: "end_point", supplier_location_id: endPoint });

              const beforeEndLocations = timingStatesMap[timing]?.beforeEndLocations || [];
              const endLocations = timingStatesMap[timing]?.endLocations || [];

              if (timingStatesMap[timing]?.addStopsBeforeEndPoint) {
                beforeEndLocations.forEach((location) => {
                  const val = values[field(`before_end_point_${location.id}`)];
                  if (val) stops.push({ type: "before_end", supplier_location_id: val });
                });
              }

              if (timingStatesMap[timing]?.addStopsAfterEndPoint) {
                endLocations.forEach((location) => {
                  const val = values[field(`end_point_${location.id}`)];
                  if (val) stops.push({ type: "after_end", supplier_location_id: val });
                });
              }
            } else if (timingTourType === "return_to_origin") {
              const beforeEndLocations = timingStatesMap[timing]?.beforeEndLocations || [];
              const endLocations = timingStatesMap[timing]?.endLocations || [];

              if (timingStatesMap[timing]?.addStopsBeforeEndPoint) {
                beforeEndLocations.forEach((location) => {
                  const val = values[field(`before_end_point_${location.id}`)];
                  if (val) stops.push({ type: "before_end", supplier_location_id: val });
                });
              }

              if (timingStatesMap[timing]?.addStopsAfterEndPoint) {
                endLocations.forEach((location) => {
                  const val = values[field(`end_point_${location.id}`)];
                  if (val) stops.push({ type: "after_end", supplier_location_id: val });
                });
              }
            }
            return {
              delivery_timing: timing,
              tour_type: timingTourType,
              route_type: timingRouteType,
              supplier_location_id: supplierLocationId,
              end_point: endPoint,
              stops
            };
          });
          submitValues.supplier_location_id =
            submitValues.custom_routes?.[0]?.supplier_location_id || null;
        }
        delete submitValues.custom_routes;
        if (values.tour_type_category === "pickup") {
          delete submitValues.color;
        }
        await onSubmit(submitValues);
      } catch (err) {
        setSubmitting(false);
      }
    },
  });
  const {
    values,
    handleChange,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
    setFieldValue,
    setErrors,
    setValues,
    setTouched,
  } = formik;
  const isPickupTour = values.tour_type_category === "pickup";
  const isMixTour = values.tour_type_category === "mix";
  const isDeliveryTour = values.tour_type_category === "delivery";
  const hasPickupRoutes = isPickupTour || isMixTour;
  console.log('errors', errors, 'values', values);

  const allowedSupplierLocationIds = useMemo(() => {
    const ids = Array.isArray(values.allowed_supplier_locations)
      ? values.allowed_supplier_locations
        .map((item) => (typeof item === "object" && item?.id ? item.id : item))
        .filter(Boolean)
      : [];
    return new Set(ids);
  }, [values.allowed_supplier_locations]);

  const allowedSupplierLocations = useMemo(() => {
    const ids = allowedSupplierLocationIds;
    if (!ids || ids.size === 0) return [];
    return supplierLocations.filter((location) => ids.has(location.id));
  }, [supplierLocations, allowedSupplierLocationIds]);

  const getSupplierLocationName = (locationId) =>
    supplierLocations.find((location) => location.id === locationId)?.name || locationId;
  const updatePickupRouteConfig = (timing, updates) => {
    setPickupRoutesMap((prev) => {
      const current = prev[timing] || { pickup_start: null, pickup_end: null, stops: [] };
      const next = { ...current, ...updates };

      if ("pickup_start" in updates || "pickup_end" in updates) {
        next.stops = (next.stops || []).filter(
          (stopId) => stopId && stopId !== next.pickup_start && stopId !== next.pickup_end
        );
      }

      return { ...prev, [timing]: next };
    });

    if (
      (updates.pickup_start || updates.pickup_end) &&
      pickupStopSelections[timing] &&
      (pickupStopSelections[timing] === updates.pickup_start ||
        pickupStopSelections[timing] === updates.pickup_end)
    ) {
      setPickupStopSelections((prev) => ({ ...prev, [timing]: "" }));
    }
  };

  const handlePickupRouteFieldChange = (timing, field, value) => {
    updatePickupRouteConfig(timing, { [field]: value });
    if (isMixTour && field === "pickup_end" && !mixSyncRef.current) {
      mixSyncRef.current = true;
      if (values.custom_route_enabled) {
        handleRouteConfigChange(timing, "supplier_location_id", value);
      } else {
        setFieldValue("supplier_location_id", value);
      }
      mixSyncRef.current = false;
    }
  };

  const handlePickupStopSelectionChange = (timing, value) => {
    setPickupStopSelections((prev) => ({ ...prev, [timing]: value }));
  };

  const handleAddPickupStopForTiming = (timing) => {
    const stopId = pickupStopSelections[timing];
    if (!stopId) return;

    setPickupRoutesMap((prev) => {
      const current = prev[timing] || { pickup_start: null, pickup_end: null, stops: [] };
      if (
        current.stops?.includes(stopId) ||
        stopId === current.pickup_start ||
        stopId === current.pickup_end
      ) {
        return prev;
      }
      return {
        ...prev,
        [timing]: { ...current, stops: [...(current.stops || []), stopId] },
      };
    });

    setPickupStopSelections((prev) => ({ ...prev, [timing]: "" }));
  };

  const handleRemovePickupStopForTiming = (timing, stopId) => {
    setPickupRoutesMap((prev) => {
      const current = prev[timing] || { pickup_start: null, pickup_end: null, stops: [] };
      return {
        ...prev,
        [timing]: {
          ...current,
          stops: (current.stops || []).filter((id) => id !== stopId),
        },
      };
    });
  };

  const handleMovePickupStopForTiming = (timing, index, direction) => {
    setPickupRoutesMap((prev) => {
      const current = prev[timing] || { pickup_start: null, pickup_end: null, stops: [] };
      const stops = [...(current.stops || [])];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= stops.length) {
        return prev;
      }
      [stops[index], stops[newIndex]] = [stops[newIndex], stops[index]];
      return {
        ...prev,
        [timing]: { ...current, stops },
      };
    });
  };

  useEffect(() => {
    if (!allowedSupplierLocationIds || allowedSupplierLocationIds.size === 0) {
      return;
    }

    setPickupRoutesMap((prev) => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach((timing) => {
        const current = next[timing];
        if (!current) return;

        const pickup_start = current.pickup_start && allowedSupplierLocationIds.has(current.pickup_start)
          ? current.pickup_start
          : null;
        const pickup_end = current.pickup_end && allowedSupplierLocationIds.has(current.pickup_end)
          ? current.pickup_end
          : null;
        const stops = (current.stops || []).filter((id) => allowedSupplierLocationIds.has(id));

        if (
          pickup_start !== current.pickup_start ||
          pickup_end !== current.pickup_end ||
          stops.length !== (current.stops || []).length
        ) {
          changed = true;
          next[timing] = { ...current, pickup_start, pickup_end, stops };
        }
      });

      return changed ? next : prev;
    });

    if (values.custom_route_enabled) {
      (values.delivery_timings || []).forEach((timing) => {
        const supplierKey = `${timing}_supplier_location_id`;
        const endKey = `${timing}_end_point`;
        const supplierVal = values[supplierKey];
        const endVal = values[endKey];
        if (supplierVal && !allowedSupplierLocationIds.has(supplierVal)) {
          setFieldValue(supplierKey, "", false);
        }
        if (endVal && !allowedSupplierLocationIds.has(endVal)) {
          setFieldValue(endKey, "", false);
        }
      });
    } else {
      if (values.supplier_location_id && !allowedSupplierLocationIds.has(values.supplier_location_id)) {
        setFieldValue("supplier_location_id", "", false);
      }
      if (values.end_point && !allowedSupplierLocationIds.has(values.end_point)) {
        setFieldValue("end_point", "", false);
      }
    }
  }, [
    allowedSupplierLocationIds,
    values.custom_route_enabled,
    values.delivery_timings,
    values.supplier_location_id,
    values.end_point,
  ]);
  let { handleBlur } = formik;
  const [addStopsAfterEndPoint, setAddStopsAfterEndPoint] = useState(false);
  const [addStopsBeforeEndPoint, setAddStopsBeforeEndPoint] = useState(false);
  const [timingStatesMap, setTimingStatesMap] = useState({});

  const setTimingState = (timing, key, value) => {
    setTimingStatesMap(prev => ({
      ...prev,
      [timing]: { ...(prev[timing] || {}), [key]: value },
    }));
  };

  const initialPickupRoutes = useMemo(() => {
    if (!initialValues) {
      return {};
    }

    let timingConfigs = initialValues.delivery_timing_configs;
    if (typeof timingConfigs === "string") {
      try {
        timingConfigs = JSON.parse(timingConfigs);
      } catch (err) {
        timingConfigs = [];
      }
    }

    if (Array.isArray(timingConfigs) && timingConfigs.length > 0) {
      const map = timingConfigs.reduce((acc, config) => {
        if (!config?.delivery_timing) {
          return acc;
        }
        let pickupStops = config?.pickup_stops;
        if (typeof pickupStops === "string") {
          try {
            pickupStops = JSON.parse(pickupStops);
          } catch (err) {
            pickupStops = [];
          }
        }
        acc[config.delivery_timing] = {
          pickup_start: config.pickup_start || null,
          pickup_end: config.pickup_end || null,
          stops: normalizePickupStops(pickupStops),
        };
        return acc;
      }, {});
      if (Object.keys(map).length > 0) {
        return map;
      }
    }

    let pickupRoutes = initialValues.pickup_routes;
    if (typeof pickupRoutes === "string") {
      try {
        pickupRoutes = JSON.parse(pickupRoutes);
      } catch (err) {
        pickupRoutes = [];
      }
    }

    if (Array.isArray(pickupRoutes) && pickupRoutes.length > 0) {
      return pickupRoutes.reduce((acc, route) => {
        if (!route?.delivery_timing) {
          return acc;
        }
        acc[route.delivery_timing] = {
          pickup_start: route.pickup_start || null,
          pickup_end: route.pickup_end || null,
          stops: Array.isArray(route.stops) ? [...route.stops] : [],
        };
        return acc;
      }, {});
    }

    if (
      initialValues.pickup_start ||
      initialValues.pickup_end ||
      (Array.isArray(initialValues.pickup_route_stops) && initialValues.pickup_route_stops.length > 0)
    ) {
      const timings = getTourDeliveryTimings(initialValues);
      return timings.reduce((acc, timing) => {
        acc[timing] = {
          pickup_start: initialValues.pickup_start || null,
          pickup_end: initialValues.pickup_end || null,
          stops: Array.isArray(initialValues.pickup_route_stops) ? [...initialValues.pickup_route_stops] : [],
        };
        return acc;
      }, {});
    }

    return {};
  }, [initialValues]);

  useEffect(() => {
    if (values.custom_route_enabled) {
      const map = {};
      (values.delivery_timings || []).forEach((t) => {
        map[t] = {
          ...(timingStatesMap[t] || {}),
          addStopsAfterEndPoint: timingStatesMap[t]?.addStopsAfterEndPoint || false,
          addStopsBeforeEndPoint: timingStatesMap[t]?.addStopsBeforeEndPoint || false,
          endLocations: timingStatesMap[t]?.endLocations || [],
          beforeEndLocations: timingStatesMap[t]?.beforeEndLocations || [],
        };
      });
      setTimingStatesMap(map);
    } else {
      setTimingStatesMap({});
    }
  }, [values.custom_route_enabled, values.delivery_timings]);

  useEffect(() => {
    if (!hasPickupRoutes) {
      setPickupRoutesMap({});
      setPickupStopSelections({});
      return;
    }

    const timings = values.delivery_timings || [];

    setPickupRoutesMap((prev) => {
      const next = { ...prev };
      const shouldSeed = Object.keys(prev).length === 0 && Object.keys(initialPickupRoutes).length > 0;

      timings.forEach((timing) => {
        if (!next[timing]) {
          const seeded = shouldSeed ? initialPickupRoutes[timing] : null;
          const defaultPickupEnd = isMixTour
            ? (values.custom_route_enabled
              ? values[`${timing}_supplier_location_id`]
              : values.supplier_location_id)
            : null;
          next[timing] = seeded
            ? {
              pickup_start: seeded.pickup_start || null,
              pickup_end: seeded.pickup_end || defaultPickupEnd || null,
              stops: Array.isArray(seeded.stops) ? [...seeded.stops] : [],
            }
            : { pickup_start: null, pickup_end: defaultPickupEnd || null, stops: [] };
        } else if (isMixTour) {
          const defaultPickupEnd = values.custom_route_enabled
            ? values[`${timing}_supplier_location_id`]
            : values.supplier_location_id;
          if (defaultPickupEnd && next[timing].pickup_end !== defaultPickupEnd) {
            next[timing] = { ...next[timing], pickup_end: defaultPickupEnd };
          }
        }
      });

      Object.keys(next).forEach((timing) => {
        if (!timings.includes(timing)) {
          delete next[timing];
        }
      });

      return next;
    });

    setPickupStopSelections((prev) => {
      const next = { ...prev };

      timings.forEach((timing) => {
        if (!(timing in next)) {
          next[timing] = "";
        }
      });

      Object.keys(next).forEach((timing) => {
        if (!timings.includes(timing)) {
          delete next[timing];
        }
      });

      return next;
    });
  }, [hasPickupRoutes, isMixTour, values.delivery_timings, values.custom_route_enabled, values.supplier_location_id, initialPickupRoutes]);

  useEffect(() => {
    if (!isMixTour || values.custom_route_enabled) {
      return;
    }
    const deliveryStart = values.supplier_location_id || null;
    if (!deliveryStart || mixSyncRef.current) {
      return;
    }
    mixSyncRef.current = true;
    setPickupRoutesMap((prev) => {
      const next = { ...prev };
      (values.delivery_timings || []).forEach((timing) => {
        const current = next[timing] || { pickup_start: null, pickup_end: null, stops: [] };
        if (current.pickup_end !== deliveryStart) {
          next[timing] = { ...current, pickup_end: deliveryStart };
        }
      });
      return next;
    });
    mixSyncRef.current = false;
  }, [isMixTour, values.custom_route_enabled, values.supplier_location_id, values.delivery_timings]);

  if (!submitCount) {
    handleBlur = null;
  }

  useEffect(() => {
    if (submitCount > 0) {
      const validateForm = async () => {
        try {
          let validationPayload = { ...values };
          const isPickupTour = values.tour_type_category === "pickup";
          const isMixTour = values.tour_type_category === "mix";
          const hasPickupRoutes = isPickupTour || isMixTour;
          if (!isPickupTour && values.custom_route_enabled && Array.isArray(values.delivery_timings)) {
            const custom_routes = values.delivery_timings.map((timing) => {
              const field = (base) => `${base}_${timing}`;
              const timingTourType = values[field("tour_type")] || values.tour_type;
              const timingRouteType = values[field("route_type")] || values.route_type;
              const supplierLocationId = values[field("supplier_location_id")] || values.supplier_location_id;
              const endPoint = values[field("end_point")] || values.end_point;

              let stops = [];
              if (timingTourType === "custom") {
                if (endPoint) stops.push({ type: "end_point", supplier_location_id: endPoint });

                const beforeEndLocations = timingStatesMap[timing]?.beforeEndLocations || [];
                const endLocations = timingStatesMap[timing]?.endLocations || [];

                if (timingStatesMap[timing]?.addStopsBeforeEndPoint) {
                  beforeEndLocations.forEach((location) => {
                    const val = values[field(`before_end_point_${location.id}`)];
                    if (val) stops.push({ type: "before_end", supplier_location_id: val });
                  });
                }

                if (timingStatesMap[timing]?.addStopsAfterEndPoint) {
                  endLocations.forEach((location) => {
                    const val = values[field(`end_point_${location.id}`)];
                    if (val) stops.push({ type: "after_end", supplier_location_id: val });
                  });
                }
              } else if (timingTourType === "return_to_origin") {
                const beforeEndLocations = timingStatesMap[timing]?.beforeEndLocations || [];
                const endLocations = timingStatesMap[timing]?.endLocations || [];

                if (timingStatesMap[timing]?.addStopsBeforeEndPoint) {
                  beforeEndLocations.forEach((location) => {
                    const val = values[field(`before_end_point_${location.id}`)];
                    if (val) stops.push({ type: "before_end", supplier_location_id: val });
                  });
                }

                if (timingStatesMap[timing]?.addStopsAfterEndPoint) {
                  endLocations.forEach((location) => {
                    const val = values[field(`end_point_${location.id}`)];
                    if (val) stops.push({ type: "after_end", supplier_location_id: val });
                  });
                }
              }

              return {
                delivery_timing: timing,
                tour_type: timingTourType,
                route_type: timingRouteType,
                supplier_location_id: supplierLocationId,
                end_point: endPoint,
                stops
              };
            });

            validationPayload.custom_routes = custom_routes;
          } else if (!isPickupTour) {
            let stops = [];
            if (values.tour_type === "custom") {
              if (values.end_point) {
                stops.push({ type: "end_point", supplier_location_id: values.end_point });
              }
              if (addStopsBeforeEndPoint && Array.isArray(beforeEndLocations)) {
                beforeEndLocations.forEach((location) => {
                  const val = values[`before_end_point_${location.id}`];
                  if (val) {
                    stops.push({ type: "before_end", supplier_location_id: val });
                  }
                });
              }
              if (addStopsAfterEndPoint && Array.isArray(endLocations)) {
                endLocations.forEach((location) => {
                  const val = values[`end_point_${location.id}`];
                  if (val) {
                    stops.push({ type: "after_end", supplier_location_id: val });
                  }
                });
              }
            } else if (values.tour_type === "return_to_origin") {
              if (addStopsBeforeEndPoint && Array.isArray(beforeEndLocations)) {
                beforeEndLocations.forEach((location) => {
                  const val = values[`before_end_point_${location.id}`];
                  if (val) {
                    stops.push({ type: "before_end", supplier_location_id: val });
                  }
                });
              }
              if (addStopsAfterEndPoint && Array.isArray(endLocations)) {
                endLocations.forEach((location) => {
                  const val = values[`end_point_${location.id}`];
                  if (val) {
                    stops.push({ type: "after_end", supplier_location_id: val });
                  }
                });
              }
            }

            if (values.tour_type === "one_way") {
              stops = [];
            }

            validationPayload.stops = stops;
          } else {
            validationPayload.stops = [];
          }

          await TourSchema.validate(validationPayload, { abortEarly: false });

          const customValidationErrors = {
            ...validateTourIdUniqueness(values),
            ...validatePickupRoutes(values),
            ...validateRouteConfigs(values),
          };
          if (Object.keys(customValidationErrors).length === 0) {
            setErrors({});
          } else {
            setErrors(customValidationErrors);
          }
        } catch (validationError) {
          if (validationError.inner) {
            const formikErrors = {};
            validationError.inner.forEach((err) => {
              if (err.path) formikErrors[err.path] = err.message;
            });

            const customValidationErrors = {
              ...validateTourIdUniqueness(values),
              ...validatePickupRoutes(values),
              ...validateRouteConfigs(values),
            };
            const mergedErrors = { ...formikErrors, ...customValidationErrors };
            setErrors(mergedErrors);
          }
        }
      };
      validateForm();
    }
  }, [values, submitCount, endLocations, beforeEndLocations, timingStatesMap, pickupRoutesMap]);

  const handleDeliveryTimingsChange = (event) => {
    const value = event.target.value;
    setFieldValue("delivery_timings", value);

    const currentConfigs = values.delivery_timing_configs || [];
    const newConfigs = value.map(timing => {
      const existingConfig = currentConfigs.find(config => config.delivery_timing === timing);
      if (existingConfig) {
        return existingConfig;
      }
      return {
        delivery_timing: timing,
        scanning_codes_enabled: false,
        allow_manual_orders_marking: false,
        bypass_route_start_verification: false,
        num_vehicles: 1,
        supplier_location_id: values.supplier_location_id || null,
        pickup_start: null,
        pickup_end: null,
        pickup_stops: [],
      };
    });

    const removedConfigs = currentConfigs.filter(config =>
      !value.includes(config.delivery_timing)
    );

    setFieldValue("delivery_timing_configs", newConfigs);
  };

  const handleAllowedSupplierLocationsChange = (event) => {
    const selectedIds = event.target.value.map((item) => (typeof item === "object" && item.id ? item.id : item));
    const allowedSet = new Set(selectedIds.map(Number));

    setFieldValue("allowed_supplier_locations", selectedIds);

    if (values.supplier_location_id && !allowedSet.has(Number(values.supplier_location_id))) {
      setFieldValue("supplier_location_id", "");
    }
    if (values.end_point && !allowedSet.has(Number(values.end_point))) {
      setFieldValue("end_point", "");
    }

    Object.keys(values).forEach((key) => {
      if (
        (key.includes("before_end_point") || key.includes("end_point")) &&
        key !== "end_point" &&
        values[key]
      ) {
        if (!allowedSet.has(Number(values[key]))) {
          setFieldValue(key, "");
        }
      }
    });

    setRouteConfigs((prev) => {
      const updated = { ...prev };
      let changed = false;
      for (const timing of Object.keys(updated)) {
        const cfg = updated[timing];
        if (!cfg) continue;

        let cfgChanged = false;
        const newCfg = { ...cfg };

        if (newCfg.supplier_location_id && !allowedSet.has(Number(newCfg.supplier_location_id))) {
          newCfg.supplier_location_id = "";
          cfgChanged = true;
        }
        if (newCfg.route_start && !allowedSet.has(Number(newCfg.route_start))) {
          newCfg.route_start = "";
          cfgChanged = true;
        }
        if (newCfg.route_end && !allowedSet.has(Number(newCfg.route_end))) {
          newCfg.route_end = "";
          cfgChanged = true;
        }
        if (newCfg.end_point && !allowedSet.has(Number(newCfg.end_point))) {
          newCfg.end_point = "";
          cfgChanged = true;
        }
        if (Array.isArray(newCfg.fix_stops)) {
          const validStops = newCfg.fix_stops.filter((stop) => {
            const locId = typeof stop === "object" ? (stop.id || stop.supplier_location_id) : stop;
            return allowedSet.has(Number(locId));
          });
          if (validStops.length !== newCfg.fix_stops.length) {
            newCfg.fix_stops = validStops;
            cfgChanged = true;
          }
        }

        if (cfgChanged) {
          updated[timing] = newCfg;
          changed = true;
        }
      }
      return changed ? updated : prev;
    });

    setPickupRoutesMap((prev) => {
      const updated = { ...prev };
      let changed = false;
      for (const timing of Object.keys(updated)) {
        const cfg = updated[timing];
        if (!cfg) continue;

        let cfgChanged = false;
        const newCfg = { ...cfg };

        if (newCfg.pickup_start && !allowedSet.has(Number(newCfg.pickup_start))) {
          newCfg.pickup_start = null;
          cfgChanged = true;
        }
        if (newCfg.pickup_end && !allowedSet.has(Number(newCfg.pickup_end))) {
          newCfg.pickup_end = null;
          cfgChanged = true;
        }
        if (Array.isArray(newCfg.stops)) {
          const validStops = newCfg.stops.filter((stopId) => allowedSet.has(Number(stopId)));
          if (validStops.length !== newCfg.stops.length) {
            newCfg.stops = validStops;
            cfgChanged = true;
          }
        }

        if (cfgChanged) {
          updated[timing] = newCfg;
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  };

  const handleTimingConfigChange = (index, field, value) => {
    const newConfigs = [...values.delivery_timing_configs];
    newConfigs[index] = { ...newConfigs[index], [field]: value };

    if (field === "scanning_codes_enabled" && !value) {
      newConfigs[index].allow_manual_orders_marking = false;
      newConfigs[index].bypass_route_start_verification = false;
    }

    if (field === "allow_manual_orders_marking" && !value) {
      newConfigs[index].bypass_route_start_verification = false;
    }

    setFieldValue("delivery_timing_configs", newConfigs);
  };

  const hasTourTypeProgress = () => {
    const hasGeneral = Boolean(values.name || values.description || values.user_defined_tour_id);
    const hasTimings = Array.isArray(values.delivery_timings) && values.delivery_timings.length > 0;
    const hasPickupProgress = Object.values(pickupRoutesMap).some(
      (route) => route.pickup_start || route.pickup_end || (route.stops || []).length > 0
    );
    const hasDeliveryProgress =
      Boolean(values.supplier_location_id || values.end_point) ||
      Boolean(values.custom_route_enabled) ||
      (values.stops || []).length > 0;

    return hasGeneral || hasTimings || hasPickupProgress || hasDeliveryProgress;
  };

  const resetPickupState = (syncFormik = true) => {
    setPickupRoutesMap({});
    setPickupStopSelections({});
    if (syncFormik) {
      setFieldValue("pickup_routes", [], false);
      setFieldValue("pickup_start", null, false);
      setFieldValue("pickup_end", null, false);
      setFieldValue("pickup_route_stops", [], false);
    }
  };

  const resetDeliveryState = (syncFormik = true) => {
    setRouteConfigs({});
    setTimingStatesMap({});
    setAddStopsAfterEndPoint(false);
    setAddStopsBeforeEndPoint(false);
    setEndLocations([]);
    setBeforeEndLocations([]);
    if (syncFormik) {
      setFieldValue("custom_route_enabled", false, false);
      setFieldValue("tour_type", "one_way", false);
      setFieldValue("route_type", "distances", false);
      setFieldValue("supplier_location_id", null, false);
      setFieldValue("end_point", null, false);
      setFieldValue("stops", [], false);
    }
  };

  const buildBaseValuesForTourType = (nextCategory) => {
    const baseValues = {
      ...formik.initialValues,
      tour_type_category: nextCategory,
      pickup_routes: [],
      pickup_start: null,
      pickup_end: null,
      pickup_route_stops: [],
      custom_route_enabled: false,
      stops: [],
      end_point: null,
      supplier_location_id: null,
      tour_type: "one_way",
      route_type: "distances",
      delivery_timings: [],
      delivery_timing_configs: [],
    };

    if (action === "ADD") {
      baseValues.allowed_supplier_locations = supplierLocations.map((supplierLocation) => supplierLocation.id);
    }

    return baseValues;
  };

  const applyTourCategoryChange = (nextCategory, currentCategory, resetAll = false) => {
    if (resetAll) {
      const baseValues = buildBaseValuesForTourType(nextCategory);
      setValues(baseValues, false);
      setErrors({});
      setTouched({}, false);
      resetPickupState(false);
      resetDeliveryState(false);
      return;
    }

    setValues((prev) => {
      const nextValues = { ...prev, tour_type_category: nextCategory };
      if (nextCategory === "pickup" && prev.tour_type_category !== "pickup") {
        nextValues.custom_route_enabled = false;
        nextValues.tour_type = "one_way";
        nextValues.route_type = "distances";
        nextValues.supplier_location_id = null;
        nextValues.end_point = null;
        nextValues.stops = [];
      }
      if (nextCategory === "delivery" && prev.tour_type_category !== "delivery") {
        nextValues.pickup_routes = [];
        nextValues.pickup_start = null;
        nextValues.pickup_end = null;
        nextValues.pickup_route_stops = [];
      }
      return nextValues;
    }, false);

    if (nextCategory === "pickup" && currentCategory !== "pickup") {
      resetDeliveryState(false);
    }

    if (nextCategory === "delivery" && currentCategory !== "delivery") {
      resetPickupState(false);
    }
  };

  const closeTourTypeDialog = () => {
    setTourTypeDialog({ open: false, message: "", nextCategory: null, resetAll: false, resetVrp: false });
  };

  const confirmTourTypeDialog = () => {
    if (!tourTypeDialog.nextCategory) {
      closeTourTypeDialog();
      return;
    }
    const currentCategory = values.tour_type_category;
    const nextCategory = tourTypeDialog.nextCategory;
    const resetAll = tourTypeDialog.resetAll;
    const resetVrp = tourTypeDialog.resetVrp;
    closeTourTypeDialog();
    if (resetVrp) {
      confirmVrpResetRef.current = true;
      const resetConfigs = (values.delivery_timing_configs || []).map((config) => ({
        ...config,
        num_vehicles: 1,
      }));
      setFieldValue("delivery_timing_configs", resetConfigs, false);
    }
    applyTourCategoryChange(nextCategory, currentCategory, resetAll);
  };

  const handleTourCategoryChange = (event) => {
    const raw = event?.target?.value ?? event;
    const nextCategory = (raw && typeof raw === "object" && "value" in raw) ? raw.value : raw;

    const currentCategory = values.tour_type_category;
    if (!nextCategory || nextCategory === currentCategory) {
      return;
    }

    const isEdit = action === "EDIT";
    const disallowedEditSwitch =
      isEdit &&
      ((currentCategory === "delivery" && nextCategory === "pickup") ||
        (currentCategory === "pickup" && nextCategory === "delivery") ||
        (currentCategory === "mix" && nextCategory === "pickup"));
    if (disallowedEditSwitch) {
      return;
    }

    const isSwitchToPickupFromNonPickup = nextCategory === "pickup" && currentCategory !== "pickup";
    const isSwitchToDeliveryFromPickup = nextCategory === "delivery" && currentCategory === "pickup";
    const isSwitchBetweenPickupAndDelivery = isSwitchToPickupFromNonPickup || isSwitchToDeliveryFromPickup;
    const isSwitchFromMixToDelivery = currentCategory === "mix" && nextCategory === "delivery";
    const isSwitchFromDeliveryToMix = currentCategory === "delivery" && nextCategory === "mix";
    const hasProgress = hasTourTypeProgress();
    const shouldResetAll = action === "ADD" && isSwitchBetweenPickupAndDelivery && hasProgress;

    let confirmMessage = null;
    let resetVrp = false;
    if (isSwitchFromDeliveryToMix) {
      const hasVrp = (values.delivery_timing_configs || []).some(
        (config) => (config.num_vehicles ?? 1) > 1,
      );
      if (hasVrp) {
        confirmMessage = t(
          "Changing the tour type to 'Mix Tour' will remove the multi-vehicle logic (VRP) and reset the number of delivery vehicles for all departures to 1. Do you want to continue?",
        );
        resetVrp = true;
      }
    } else if (isSwitchFromMixToDelivery) {
      confirmMessage = t("Switching Tour Type to Delivery will remove all pickup-related settings. Continue?");
    } else if (isSwitchBetweenPickupAndDelivery) {
      confirmMessage = t("Switching Pickup Type will discard all current progress. Continue?");
    }

    if (confirmMessage && (resetVrp || hasProgress)) {
      setTourTypeDialog({
        open: true,
        message: confirmMessage,
        nextCategory,
        resetAll: shouldResetAll,
        resetVrp,
      });
      return;
    }

    applyTourCategoryChange(nextCategory, currentCategory, shouldResetAll);
  };

  const closeTourHandler = () => {
    action === "ADD" ? history.push(PATHS.tours.root) : history.push(PATHS.tours.detail.replace(":id", id));
  };

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const lastElementRef = useRef(null);
  const deliveryTimings = sortOrder;
  const tourTypeOptions = [
    {
      label: t("Delivery Tour"),
      value: "delivery",
      disabled: action === "EDIT" && values.tour_type_category === "pickup",
    },
    {
      label: t("Pickup Tour"),
      value: "pickup",
      disabled: action === "EDIT" && (values.tour_type_category === "delivery" || values.tour_type_category === "mix"),
    },
    { label: t("Mix Tour"), value: "mix" },
  ];
  const handoverTypeOptions = [
    { label: t("Paper based"), value: "paper" },
    { label: t("Digitalised"), value: "digital" },
  ];

  useEffect(() => {
    setAvailableSupplierLocations(
      supplierLocations.map((supplierLocation) => ({
        id: supplierLocation.id,
        name: supplierLocation.name,
      }))
    );

    if (action === "ADD") {
      setFieldValue("allowed_supplier_locations", []);
    }
  }, [supplierLocations, action]);
  useEffect(() => {
    const handleTabPress = (e) => {
      if (e.key === "Tab") {
        if (lastElementRef.current && lastElementRef.current.contains(document.activeElement)) {
          e.preventDefault();
          if (cancelButtonRef.current) {
            cancelButtonRef.current.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleTabPress);
    return () => {
      document.removeEventListener("keydown", handleTabPress);
    };
  }, []);

  const handleAddEndLocation = () => {
    setEndLocations((prev) => [...prev, { id: prev.length + 1 }]);
  };

  useEffect(() => {
    if (initialValues && Array.isArray(initialValues.stops) && initialValues.stops.length) {
      if (initialValues.tour_type === "one_way") {
        setAddStopsAfterEndPoint(false);
        setAddStopsBeforeEndPoint(false);
        setEndLocations([]);
        setBeforeEndLocations([]);
        return;
      }
      const afterEndStops = initialValues.stops.filter((s) => s.type === "after_end");
      const beforeEndStops = initialValues.stops.filter((s) => s.type === "before_end");
      const endPointStop = initialValues.stops.find((s) => s.type === "end_point");

      if (afterEndStops && afterEndStops.length === 0) {
        setAddStopsAfterEndPoint(false);
      } else {
        setAddStopsAfterEndPoint(true);
      }

      if (beforeEndStops && beforeEndStops.length === 0) {
        setAddStopsBeforeEndPoint(false);
      } else {
        setAddStopsBeforeEndPoint(true);
      }

      setEndLocations(afterEndStops.map((s, idx) => ({ id: idx + 1 })));
      setBeforeEndLocations(beforeEndStops.map((s, idx) => ({ id: idx + 1 })));

      afterEndStops.forEach((s, idx) => {
        setFieldValue(`end_point_${idx + 1}`, s.supplier_location_id, false);
      });

      beforeEndStops.forEach((s, idx) => {
        setFieldValue(`before_end_point_${idx + 1}`, s.supplier_location_id, false);
      });

      if (endPointStop && initialValues.tour_type === "custom") {
        setFieldValue("end_point", endPointStop.supplier_location_id, false);
      }
    }
  }, [initialValues]);

  useEffect(() => {
    if (initialValues && action === "EDIT") {
      if (initialValues.delivery_timing_configs && initialValues.delivery_timing_configs.length > 0) {
        const seededConfigs = initialValues.delivery_timing_configs.map((config) => ({
          ...config,
          supplier_location_id: config.supplier_location_id || initialValues.supplier_location_id || null,
        }));
        setFieldValue("delivery_timing_configs", seededConfigs);

        if (initialValues.custom_route_enabled) {
          const newTimingStatesMap = {};

          initialValues.delivery_timing_configs.forEach(config => {
            const timing = config.delivery_timing;
            const field = (base) => `${base}_${timing}`;

            if (config.route_type) {
              setFieldValue(field("route_type"), config.route_type, false);
            }

            setFieldValue(field("tour_type"), "one_way", false);
            setFieldValue(field("supplier_location_id"), initialValues.supplier_location_id, false);

            newTimingStatesMap[timing] = {
              addStopsBeforeEndPoint: false,
              addStopsAfterEndPoint: false,
              beforeEndLocations: [],
              endLocations: []
            };
          });

          setTimingStatesMap(newTimingStatesMap);
        }
      } else if (initialValues.delivery_timings && initialValues.delivery_timings.length > 0) {
        const migratedConfigs = initialValues.delivery_timings.map(timing => ({
          delivery_timing: timing,
          scanning_codes_enabled: initialValues.scanning_codes_enabled || false,
          allow_manual_orders_marking: initialValues.allow_manual_orders_marking || false,
          bypass_route_start_verification: initialValues.bypass_route_start_verification || false,
          route_type: initialValues.route_type || "distances"
        }));
        setFieldValue("delivery_timing_configs", migratedConfigs);
      }
    }
  }, [initialValues, action]);

  useEffect(() => {
    if (!values.custom_route_enabled || !Array.isArray(values.delivery_timing_configs)) {
      return;
    }

    let changed = false;
    const updatedConfigs = values.delivery_timing_configs.map((config) => {
      if (config.supplier_location_id) {
        return config;
      }
      const timing = config.delivery_timing;
      const fallback =
        values[`${timing}_supplier_location_id`] ||
        routeConfigs[timing]?.supplier_location_id ||
        values.supplier_location_id ||
        null;

      if (!fallback) {
        return config;
      }

      changed = true;
      return { ...config, supplier_location_id: fallback };
    });

    if (changed) {
      setFieldValue("delivery_timing_configs", updatedConfigs, false);
    }
  }, [values.custom_route_enabled, values.delivery_timing_configs, values.supplier_location_id, routeConfigs]);

  useEffect(() => {
    let stops = [];
    if (values.tour_type === "custom") {
      if (values.end_point) {
        stops.push({ type: "end_point", supplier_location_id: values.end_point });
      }
      if (addStopsBeforeEndPoint && Array.isArray(beforeEndLocations)) {
        beforeEndLocations.forEach((location) => {
          const val = values[`before_end_point_${location.id}`];
          if (val) {
            stops.push({ type: "before_end", supplier_location_id: val });
          }
        });
      }
      if (addStopsAfterEndPoint && Array.isArray(endLocations)) {
        endLocations.forEach((location) => {
          const val = values[`end_point_${location.id}`];
          if (val) {
            stops.push({ type: "after_end", supplier_location_id: val });
          }
        });
      }
    } else if (values.tour_type === "return_to_origin") {
      if (addStopsAfterEndPoint && Array.isArray(endLocations)) {
        endLocations.forEach((location) => {
          const val = values[`end_point_${location.id}`];
          if (val) {
            stops.push({ type: "after_end", supplier_location_id: val });
          }
        });
      }
    }
    if (values.tour_type === "one_way") {
      stops = [];
    }
    if (JSON.stringify(values.stops) !== JSON.stringify(stops)) {
      setFieldValue("stops", stops, false);
    }
  }, [
    values.tour_type,
    values.supplier_location_id,
    values.end_point,
    endLocations,
    beforeEndLocations,
    ...endLocations.map((location) => values[`end_point_${location.id}`]),
    ...beforeEndLocations.map((location) => values[`before_end_point_${location.id}`]),
  ]);

  const handleAddBeforeEndLocation = () => {
    setBeforeEndLocations((prev) => [...prev, { id: prev.length + 1 }]);
  };

  const handleRemoveEndLocation = (id) => {
    setEndLocations((prev) => prev.filter((location) => location.id !== id));
    setFieldValue(`end_point_${id}`, "", false);
  };

  const handleRemoveBeforeEndLocation = (id) => {
    setBeforeEndLocations((prev) => prev.filter((location) => location.id !== id));
    setFieldValue(`before_end_point_${id}`, "", false);
  };

  useEffect(() => {
    if (values.custom_route_enabled && values.delivery_timings && values.delivery_timings.length > 0) {
      const newConfigs = { ...routeConfigs };

      values.delivery_timings.forEach(timing => {
        const config = newConfigs[timing];
        if (config) {
          setFieldValue(`${timing}_supplier_location_id`, config.supplier_location_id, false);
          setFieldValue(`${timing}_tour_type`, config.tour_type, false);
          setFieldValue(`${timing}_route_type`, config.route_type, false);
          setFieldValue(`${timing}_end_point`, config.end_point, false);
        }
        if (!newConfigs[timing]) {
          let initialConfig = {
            tour_type: values.tour_type || 'one_way',
            route_type: values.route_type || 'distances',
            supplier_location_id: values.supplier_location_id || initialValues?.supplier_location_id || null,
            end_point: values.end_point || null,
            addStopsBeforeEndPoint: false,
            addStopsAfterEndPoint: false,
            beforeEndLocations: [],
            endLocations: []
          };

          if (action === "EDIT" && initialValues?.delivery_timing_configs) {
            const existingConfig = initialValues.delivery_timing_configs.find(c => c.delivery_timing === timing);
            if (existingConfig && existingConfig.tour_type) {
              let fixStops = [];
              if (existingConfig.fix_stops) {
                try {
                  fixStops = typeof existingConfig.fix_stops === 'string'
                    ? JSON.parse(existingConfig.fix_stops)
                    : existingConfig.fix_stops;
                } catch (e) {
                  fixStops = [];
                }
              }

              const beforeEndStops = fixStops.filter(s => s.type === "before_end") || [];
              const afterEndStops = fixStops.filter(s => s.type === "after_end") || [];

              initialConfig = {
                tour_type: existingConfig.tour_type || values.tour_type || 'one_way',
                route_type: existingConfig.route_type || values.route_type || 'distances',
                supplier_location_id: existingConfig.supplier_location_id || values.supplier_location_id || initialValues?.supplier_location_id,
                end_point: existingConfig.route_end || values.end_point || null,
                addStopsBeforeEndPoint: beforeEndStops.length > 0,
                addStopsAfterEndPoint: afterEndStops.length > 0,
                beforeEndLocations: beforeEndStops.map((s, idx) => ({ id: idx + 1 })),
                endLocations: afterEndStops.map((s, idx) => ({ id: idx + 1 }))
              };

              beforeEndStops.forEach((s, idx) => {
                setFieldValue(`${timing}_before_end_point_${idx + 1}`, s.supplier_location_id, false);
              });

              afterEndStops.forEach((s, idx) => {
                setFieldValue(`${timing}_end_point_${idx + 1}`, s.supplier_location_id, false);
              });
            }
          }

          newConfigs[timing] = initialConfig;
          setFieldValue(`${timing}_supplier_location_id`, initialConfig.supplier_location_id, false);
          setFieldValue(`${timing}_tour_type`, initialConfig.tour_type, false);
          setFieldValue(`${timing}_route_type`, initialConfig.route_type, false);
          setFieldValue(`${timing}_end_point`, initialConfig.end_point, false);
        }
      });

      Object.keys(newConfigs).forEach(timing => {
        if (!values.delivery_timings.includes(timing)) {
          delete newConfigs[timing];
        }
      });

      setRouteConfigs(newConfigs);
    } else if (!values.custom_route_enabled) {
      setRouteConfigs({});
    }
  }, [values.custom_route_enabled, values.delivery_timings, action, initialValues]);
  const handleRouteConfigChange = (timing, field, value) => {
    setRouteConfigs(prev => ({
      ...prev,
      [timing]: {
        ...prev[timing],
        [field]: value
      }
    }));
    const fieldName = `${timing}_${field}`;
    setFieldValue(fieldName, value);
    if (isMixTour && field === "supplier_location_id" && !mixSyncRef.current) {
      mixSyncRef.current = true;
      updatePickupRouteConfig(timing, { pickup_end: value });
      mixSyncRef.current = false;
    }
    const configIndex = values.delivery_timing_configs.findIndex(config =>
      config.delivery_timing === timing
    );
    if (configIndex !== -1) {
      const newConfigs = [...values.delivery_timing_configs];
      newConfigs[configIndex] = {
        ...newConfigs[configIndex],
        [field]: value
      };
      setFieldValue('delivery_timing_configs', newConfigs);
    }
    if (field === 'supplier_location_id' && value) {
      const timingIndex = values.delivery_timings.indexOf(timing);
      const errorKey = `delivery_timing_configs[${timingIndex}].supplier_location_id`;
      if (errors[errorKey]) {
        const newErrors = { ...errors };
        delete newErrors[errorKey];
        setErrors(newErrors);
      }
    }
  };
  const handleAddEndLocationForTiming = (timing) => {
    setRouteConfigs(prev => ({
      ...prev,
      [timing]: {
        ...prev[timing],
        endLocations: [...(prev[timing].endLocations || []), { id: (prev[timing].endLocations?.length || 0) + 1 }]
      }
    }));
  };

  const handleRemoveEndLocationForTiming = (timing, id) => {
    setRouteConfigs(prev => ({
      ...prev,
      [timing]: {
        ...prev[timing],
        endLocations: prev[timing].endLocations.filter(location => location.id !== id)
      }
    }));
    setFieldValue(`${timing}_end_point_${id}`, '', false);
  };

  const handleAddBeforeEndLocationForTiming = (timing) => {
    setRouteConfigs(prev => ({
      ...prev,
      [timing]: {
        ...prev[timing],
        beforeEndLocations: [...(prev[timing].beforeEndLocations || []), { id: (prev[timing].beforeEndLocations?.length || 0) + 1 }]
      }
    }));
  };

  const handleRemoveBeforeEndLocationForTiming = (timing, id) => {
    setRouteConfigs(prev => ({
      ...prev,
      [timing]: {
        ...prev[timing],
        beforeEndLocations: prev[timing].beforeEndLocations.filter(location => location.id !== id)
      }
    }));
    setFieldValue(`${timing}_before_end_point_${id}`, '', false);
  };

  const renderRouteSetupForTiming = (timing) => {
    const config = routeConfigs[timing] || {};
    const timingIndex = values.delivery_timings.indexOf(timing);
    const deliveryStartLabel = isMixTour ? t("Pickup End / Delivery Start") : t("Tour Start Location");

    const supplierLocationError = errors[`delivery_timing_configs[${timingIndex}].supplier_location_id`] ||
      errors[`${timing}_supplier_location_id`];
    const customErrors = { ...errors };
    delete customErrors[`delivery_timing_configs[${timingIndex}].supplier_location_id`];
    delete customErrors[`${timing}_supplier_location_id`];
    return (
      <Box key={timing} mb={4} p={4}>
        <Typography variant="body1" style={{ fontWeight: "bold" }}>
          {t('Route Setup for')} {t(timing)}
        </Typography>

        <Grid container spacing={2} ml={2}>
          {user?.permissions?.tours?.extended_features && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Select
                  label={t("Tour Type")}
                  name={`${timing}_tour_type`}
                  onChange={(e) => handleRouteConfigChange(timing, 'tour_type', e.target.value)}
                  value={config.tour_type || 'one_way'}
                  errors={customErrors}
                  options={[
                    { label: t("One Way"), value: "one_way" },
                    { label: t("Return to Origin"), value: "return_to_origin" },
                    { label: t("Custom"), value: "custom" },
                  ]}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Select
                  label={t("Efficient Routing Scheme")}
                  name={`${timing}_route_type`}
                  onChange={(e) => handleRouteConfigChange(timing, 'route_type', e.target.value)}
                  value={config.route_type || 'distances'}
                  errors={customErrors}
                  options={[
                    { label: t("Shortest Distance Mapbox"), value: "distances" },
                    { label: t("Shortest Duration Mapbox"), value: "durations" },
                    { label: t("Shortest Distance Google"), value: "distances_google" },
                  ]}
                  required
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Select
              label={deliveryStartLabel + " *"}
              name={`${timing}_supplier_location_id`}
              onChange={(e) => handleRouteConfigChange(timing, 'supplier_location_id', e.target.value)}
              value={values[`${timing}_supplier_location_id`] || config.supplier_location_id || ''}
              errors={customErrors}
              options={allowedSupplierLocations.map((o) => ({
                label: o.name,
                value: o.id,
              }))}
              required
            />
            {supplierLocationError && (
              <Typography color="error" variant="caption" style={{ display: 'block', marginTop: '4px' }}>
                {supplierLocationError}
              </Typography>
            )}
          </Grid>
          {config.tour_type !== "one_way" && user?.permissions?.tours?.extended_features && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Select
                  label={t("Tour End Location") + " *"}
                  name={`${timing}_end_point`}
                  onChange={(e) => handleRouteConfigChange(timing, 'end_point', e.target.value)}
                  value={config.tour_type === "return_to_origin" ? config.supplier_location_id : config.end_point || ''}
                  errors={customErrors}
                  options={allowedSupplierLocations.map((o) => ({
                    label: o.name,
                    value: o.id,
                  }))}
                  disabled={config.tour_type === "return_to_origin"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Checkbox
                  checked={config.addStopsBeforeEndPoint || false}
                  value={config.addStopsBeforeEndPoint || false}
                  name={`${timing}_add_stops_before_end_point`}
                  onChange={() => handleRouteConfigChange(timing, 'addStopsBeforeEndPoint', !config.addStopsBeforeEndPoint)}
                  style={{ color: "#6F9CEB" }}
                />
                <Typography component="span" className="font-size-12">
                  {t("Add Stops Before Finish")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Checkbox
                  checked={config.addStopsAfterEndPoint || false}
                  value={config.addStopsAfterEndPoint || false}
                  name={`${timing}_add_stops_after_end_point`}
                  onChange={() => handleRouteConfigChange(timing, 'addStopsAfterEndPoint', !config.addStopsAfterEndPoint)}
                  style={{ color: "#6F9CEB" }}
                />
                <Typography component="span" className="font-size-12">
                  {t("Add Stops After Finish")}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>

        {/* Stops Before End */}
        {config.tour_type !== "one_way" && user?.permissions?.tours?.extended_features && config.addStopsBeforeEndPoint && (
          <Grid container spacing={2}>
            {(config.beforeEndLocations || []).map((location) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={2}
                key={location.id}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Select
                  label={t("Stops Before Tour End")}
                  name={`${timing}_before_end_point_${location.id}`}
                  onChange={handleChange}
                  value={values[`${timing}_before_end_point_${location.id}`] || ""}
                  errors={errors}
                  options={allowedSupplierLocations.map((o) => ({
                    label: o.name,
                    value: o.id,
                  }))}
                  required
                  style={{ flex: 1 }}
                />
                <IconButton
                  aria-label={t("Remove stop")}
                  onClick={() => handleRemoveBeforeEndLocationForTiming(timing, location.id)}
                  style={{ marginLeft: 8 }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4} lg={2} style={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={() => handleAddBeforeEndLocationForTiming(timing)}
                style={{
                  marginTop: "4px",
                  backgroundColor: "transparent",
                  borderColor: "#6F9CEB",
                  color: "#6F9CEB",
                  width: "100%",
                  height: "40px",
                }}
              >
                {t("Add Before Finish")}
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Stops After End */}
        {config.tour_type !== "one_way" && user?.permissions?.tours?.extended_features && config.addStopsAfterEndPoint && (
          <Grid container spacing={2}>
            {(config.endLocations || []).map((location) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={2}
                key={location.id}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Select
                  label={t("Stops After Tour End")}
                  name={`${timing}_end_point_${location.id}`}
                  onChange={handleChange}
                  value={values[`${timing}_end_point_${location.id}`] || ""}
                  errors={errors}
                  options={allowedSupplierLocations.map((o) => ({
                    label: o.name,
                    value: o.id,
                  }))}
                  required
                  style={{ flex: 1 }}
                />
                <IconButton
                  aria-label={t("Remove stop")}
                  onClick={() => handleRemoveEndLocationForTiming(timing, location.id)}
                  style={{ marginLeft: 8 }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4} lg={2} style={{ display: "center" }}>
              <Button
                variant="outlined"
                onClick={() => handleAddEndLocationForTiming(timing)}
                style={{
                  marginTop: "4px",
                  backgroundColor: "transparent",
                  borderColor: "#6F9CEB",
                  color: "#6F9CEB",
                  width: "100%",
                  height: "40px",
                }}
              >
                {t("Add After Finish")}
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Route Visualization inside the card */}
        <Box mt={4}>
          <TourRouteVisualization
            start={
              supplierLocations.find((o) => o.id === config.supplier_location_id)?.name ||
              "Start Location"
            }
            customerStops={values.name ? [values.name] : []}
            end={
              config.tour_type === "one_way"
                ? null
                : supplierLocations.find(
                  (o) =>
                    o.id ===
                    (config.tour_type === "return_to_origin"
                      ? config.supplier_location_id
                      : config.end_point)
                )?.name || "End Location"
            }
            stopsBeforeEnd={
              config.addStopsBeforeEndPoint && config.tour_type !== "one_way"
                ? (config.beforeEndLocations || [])
                  .map(
                    (location) =>
                      supplierLocations.find(
                        (o) => o.id === values[`${timing}_before_end_point_${location.id}`]
                      )?.name || null
                  )
                  .filter(Boolean)
                : []
            }
            stopsAfterEnd={
              config.addStopsAfterEndPoint && config.tour_type !== "one_way"
                ? (config.endLocations || [])
                  .map(
                    (location) =>
                      supplierLocations.find(
                        (o) => o.id === values[`${timing}_end_point_${location.id}`]
                      )?.name || null
                  )
                  .filter(Boolean)
                : []
            }
          />
        </Box>
      </Box>
    );
  };

  const renderPickupRouteSetupForTiming = (timing) => {
    const config = pickupRoutesMap[timing] || {};
    const stops = Array.isArray(config.stops) ? config.stops : [];
    const pickupEndLabel = isMixTour ? t("Pickup End / Delivery Start") : t("Pickup End");
    const isSharedPickupEnd = isMixTour && !values.custom_route_enabled;
    const pickupEndHelp = isSharedPickupEnd
      ? t("Pickup End is shared with Delivery Start.")
      : undefined;

    const startOptions = allowedSupplierLocations
      .filter((location) => location.id !== config.pickup_end)
      .map((location) => ({
        label: location.name,
        value: location.id,
      }));

    const endOptions = allowedSupplierLocations
      .filter((location) => location.id !== config.pickup_start)
      .map((location) => ({
        label: location.name,
        value: location.id,
      }));

    const availableStops = allowedSupplierLocations
      .filter(
        (location) =>
          location.id !== config.pickup_start &&
          location.id !== config.pickup_end &&
          !stops.includes(location.id)
      )
      .map((location) => ({
        label: location.name,
        value: location.id,
      }));

    return (
      <Box key={timing} mb={4} p={4}>
        <Typography variant="body1" style={{ fontWeight: "bold" }}>
          {t("Route Setup for")} {t(timing)}
        </Typography>

        <Grid container spacing={2} ml={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              label={t("Pickup Start") + " *"}
              name={`${timing}_pickup_start`}
              onChange={(e) => handlePickupRouteFieldChange(timing, "pickup_start", e.target.value)}
              value={config.pickup_start || ""}
              errors={errors}
              options={startOptions}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              label={pickupEndLabel + " *"}
              name={`${timing}_pickup_end`}
              onChange={(e) => handlePickupRouteFieldChange(timing, "pickup_end", e.target.value)}
              value={config.pickup_end || ""}
              errors={errors}
              options={endOptions}
              disabled={isSharedPickupEnd}
              help={pickupEndHelp}
              required
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <Grid container spacing={2} ml={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Select
                label={t("Select Stop to Add")}
                name={`${timing}_pickup_stop_selector`}
                value={pickupStopSelections[timing] || ""}
                onChange={(e) => handlePickupStopSelectionChange(timing, e.target.value)}
                options={availableStops}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Button
                variant="outlined"
                color="primary"
                disabled={!pickupStopSelections[timing]}
                onClick={() => handleAddPickupStopForTiming(timing)}
              >
                {t("Add Stop")}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {stops.length > 0 && (
          <Box mt={2} ml={2}>
            <Typography variant="subtitle2">{t("Stops")}</Typography>
            {stops.map((stopId, index) => (
              <Box
                key={stopId}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                border="1px solid #eee"
                p={1}
                mt={1}
              >
                <Typography>{getSupplierLocationName(stopId)}</Typography>
                <Box>
                  <IconButton
                    onClick={() => handleMovePickupStopForTiming(timing, index, -1)}
                    size="small"
                    disabled={index === 0}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleMovePickupStopForTiming(timing, index, 1)}
                    size="small"
                    disabled={index === stops.length - 1}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleRemovePickupStopForTiming(timing, stopId)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box mt={4}>
          <TourRouteVisualization
            start={getSupplierLocationName(config.pickup_start) || "Start"}
            customerStops={stops.map((id) => getSupplierLocationName(id)).filter(Boolean)}
            customerStopsLabel="Pickup Stops"
            end={getSupplierLocationName(config.pickup_end) || "End"}
            stopsBeforeEnd={[]}
            stopsAfterEnd={[]}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box>
        {typeof errors.stops === "string" && (
          <Typography color="error" style={{ marginBottom: 16 }}>
            {errors.stops}
          </Typography>
        )}
        {Array.isArray(errors.stops) &&
          errors.stops.map(
            (err, idx) =>
              err &&
              Object.values(err).map((msg, subIdx) => (
                <Typography color="error" style={{ marginBottom: 16 }} key={idx + "-" + subIdx}>
                  {msg}
                </Typography>
              ))
          )}
        <Box display="flex" mb={4}>
          <Box flex={2}>
            <Typography className={classes._heading} variant="h4">
              {action === "ADD" ? t("New Tour") : t("Edit Tour")}
            </Typography>
          </Box>
          <Box flex={2} textAlign="right" position="relative">
            <Box position="absolute" right={0}>
              <FormCancelSaveButton
                disabled={!isValid || isSubmitting}
                onCancel={closeTourHandler}
                onSave={handleSubmit}
                ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
              />
            </Box>
          </Box>
        </Box>
        <Typography className={classes._subheading} variant="h4">
          {t("Tour Setup")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Select
              label={t("Tour Type")}
              name="tour_type_category"
              value={values.tour_type_category}
              onChange={handleTourCategoryChange}
              options={tourTypeOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Input
              label={t("Tour Name")}
              name="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              errors={errors}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Input
              label={t("Tour ID")}
              name="user_defined_tour_id"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.user_defined_tour_id}
              errors={errors}
              required
              maxLength={3}
              type="number"
              onInput={(e) => {
                if (e.target.value.length > 3) {
                  e.target.value = e.target.value.slice(0, 3);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Input
              label={t("Remark")}
              name="description"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.description}
              errors={errors}
            />
          </Grid>
          {user?.permissions?.tours?.deactivate && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Checkbox
                checked={values.active}
                value={values.active}
                name="active"
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                style={{ color: "#6F9CEB" }}
              />
              <Typography component="span" className="font-size-12">
                {t("Active")}
              </Typography>
            </Grid>
          )}
          {!isPickupTour && user?.permissions?.tours?.deactivate && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Checkbox
                checked={values.is_default}
                value={values.is_default}
                name="is_default"
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                style={{ color: "#6F9CEB" }}
              />
              <Typography component="span" className="font-size-12">
                {t("Default Tour")}
              </Typography>
            </Grid>
          )}
          {isDeliveryTour && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Select
                label={t("Handover type")}
                name="handover_type"
                value={values.middle_mile_digital ? "digital" : "paper"}
                onChange={(event) =>
                  setFieldValue("middle_mile_digital", event.target.value === "digital")
                }
                onBlur={handleBlur}
                options={handoverTypeOptions}
              />
            </Grid>
          )}
          {!isPickupTour && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Checkbox
                checked={values.third_party}
                value={values.third_party}
                name="third_party"
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                style={{ color: "#6F9CEB" }}
              />
              <Typography component="span" className="font-size-12">
                {t("Third party")}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <div ref={lastElementRef}>
              <CustomMultiSelect
                values={deliveryTimings}
                errors={errors}
                onChange={handleDeliveryTimingsChange}
                setValues={values.delivery_timings}
                label={"Delivery Timings"}
                labelId={"delivery-timings-label"}
                backgroundColor={"#dfdfdf"}
                borderColor={"#6F9CEB"}
                required={true}
                name="delivery_timings"
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <div ref={lastElementRef}>
              <CustomMultiSelect
                values={availableSupplierLocations}
                errors={errors}
                onChange={handleAllowedSupplierLocationsChange}
                setValues={values.allowed_supplier_locations}
                label={"Allowed Supplier Locations"}
                labelId={"supplier-locations-label"}
                required={true}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                name="allowed_supplier_locations"
              />
            </div>
          </Grid>
          {!isPickupTour && (
            <Grid item xs={12} sm={6} md={4} lg={2} style={{ display: "flex", alignItems: "flex-end" }}>
              <Box width="100%" pb={0.5}>
                <ColorPicker
                  label="Tour Color"
                  value={values.color}
                  onChange={(c) => setFieldValue("color", c, false)}
                  presetColors={TOUR_COLOR_PRESETS}
                  required
                />
                {errors.color && (
                  <Typography
                    color="error"
                    variant="caption"
                    style={{ display: "block", marginTop: 4 }}
                  >
                    {t(errors.color)}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>

        {
          values.delivery_timings && values.delivery_timings.length > 0 && (
            <>
              <Typography className={classes._subheading} variant="h4" style={{ marginTop: "32px" }}>
                {isPickupTour ? t("Pickup Route Setup") : t("Delivery Timing Permissions")}
              </Typography>
              <Box mt={2}>
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item xs={12} sm={!isPickupTour && !isMixTour ? 2 : 3}>
                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                      {t("Delivery Timing")}
                    </Typography>
                  </Grid>
                  {!isPickupTour && !isMixTour && (
                    <Grid item xs={12} sm={2} style={{ textAlign: "center" }}>
                      <Typography variant="body1" style={{ fontWeight: "bold" }}>
                        {t("Vehicles")}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={!isPickupTour && !isMixTour ? 2 : 3} style={{ textAlign: "center" }}>
                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                      {t("Scan Code Enabled")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={!isPickupTour && !isMixTour ? 3 : 3} style={{ textAlign: "center" }}>
                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                      {t("Allow Manual Orders Marking")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={!isPickupTour && !isMixTour ? 3 : 3} style={{ textAlign: "center" }}>
                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                      {t("Bypass Route Start Verification")}
                    </Typography>
                  </Grid>
                </Grid>

                {values.delivery_timing_configs && [...values.delivery_timing_configs]
                  .sort((a, b) => sortOrder.indexOf(a.delivery_timing) - sortOrder.indexOf(b.delivery_timing))
                  .map((config) => {
                    const originalIndex = values.delivery_timing_configs.findIndex(c => c.delivery_timing === config.delivery_timing);
                    return (
                      <Grid container spacing={2} key={config.delivery_timing} style={{
                        padding: "8px 0",
                        borderBottom: "1px solid #e0e0e0",
                        alignItems: "center"
                      }}>
                        <Grid item xs={12} sm={!isPickupTour && !isMixTour ? 2 : 3}>
                          <Typography variant="body1" style={{ fontWeight: "bold", color: "#6F9CEB" }}>
                            {t(config.delivery_timing)}
                          </Typography>
                        </Grid>
                        {!isPickupTour && !isMixTour && (
                          <Grid item xs={12} sm={2} style={{ textAlign: "center" }}>
                            <Select
                              name={`delivery_timing_configs[${originalIndex}].num_vehicles`}
                              value={config.num_vehicles ?? 1}
                              onChange={(e) => handleTimingConfigChange(originalIndex, "num_vehicles", Number(e.target.value))}
                              options={vehicleCountOptions}
                              errors={{}}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={!isPickupTour && !isMixTour ? 2 : 3} style={{ textAlign: "center" }}>
                          <Checkbox
                            checked={config.scanning_codes_enabled || false}
                            onChange={(e) => handleTimingConfigChange(originalIndex, "scanning_codes_enabled", e.target.checked)}
                            style={{ color: "#6F9CEB" }}
                            inputProps={{ 'aria-label': 'Scan Code Enabled' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3} style={{ textAlign: "center" }}>
                          <Checkbox
                            checked={config.allow_manual_orders_marking || false}
                            disabled={!config.scanning_codes_enabled}
                            onChange={(e) => handleTimingConfigChange(originalIndex, "allow_manual_orders_marking", e.target.checked)}
                            style={{ color: "#6F9CEB" }}
                            inputProps={{ 'aria-label': 'Allow Manual Orders Marking' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3} style={{ textAlign: "center" }}>
                          <Checkbox
                            checked={config.bypass_route_start_verification || false}
                            disabled={!config.scanning_codes_enabled}
                            onChange={(e) => handleTimingConfigChange(originalIndex, "bypass_route_start_verification", e.target.checked)}
                            style={{ color: "#6F9CEB" }}
                            inputProps={{ 'aria-label': 'Bypass Route Start Verification' }}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
              </Box>
            </>
          )}

        {
          hasPickupRoutes && (
            <>
              <Typography className={classes._subheading} variant="h4">
                {t("Pickup Route Setup")}
              </Typography>
              {values.delivery_timings && values.delivery_timings.length > 0 ? (
                values.delivery_timings
                  .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
                  .map((timing) => renderPickupRouteSetupForTiming(timing))
              ) : (
                <Typography variant="body1" color="textSecondary" style={{ marginTop: "16px" }}>
                  {t("Please select Departure Times first to configure the Routes")}
                </Typography>
              )}
            </>
          )
        }

        {
          !isPickupTour && (
            <>
              <Typography className={classes._subheading} variant="h4">
                {isMixTour ? t("Delivery Route Setup") : t("Route Setup")}
              </Typography>
              <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.custom_route_enabled}
                        color="primary"
                        onChange={
                          (e) => {
                            const checked = e.target.checked;

                            if (checked) {
                              const newConfigs = {};
                              (values.delivery_timings || []).forEach((timing) => {
                                newConfigs[timing] = {
                                  tour_type: values.tour_type,
                                  route_type: values.route_type,
                                  supplier_location_id: values.supplier_location_id,
                                  end_point: values.end_point,
                                  addStopsBeforeEndPoint: addStopsBeforeEndPoint,
                                  addStopsAfterEndPoint: addStopsAfterEndPoint,
                                  beforeEndLocations: beforeEndLocations,
                                  endLocations: endLocations,
                                };

                                setFieldValue(`${timing}_supplier_location_id`, values.supplier_location_id, false);
                                setFieldValue(`${timing}_tour_type`, values.tour_type, false);
                                setFieldValue(`${timing}_route_type`, values.route_type, false);
                                setFieldValue(`${timing}_end_point`, values.end_point, false);

                                (beforeEndLocations || []).forEach((loc) => {
                                  const currentVal = values[`before_end_point_${loc.id}`];
                                  if (currentVal !== undefined) {
                                    setFieldValue(`${timing}_before_end_point_${loc.id}`, currentVal, false);
                                  }
                                });
                                (endLocations || []).forEach((loc) => {
                                  const currentVal = values[`end_point_${loc.id}`];
                                  if (currentVal !== undefined) {
                                    setFieldValue(`${timing}_end_point_${loc.id}`, currentVal, false);
                                  }
                                });
                              });

                              setRouteConfigs(newConfigs);
                              setFieldValue("routeConfigs", newConfigs, false);
                            } else {
                              setRouteConfigs({});
                              setFieldValue("routeConfigs", {}, false);
                            }

                            setFieldValue("custom_route_enabled", checked);
                          }
                        }
                      />
                    }
                    label={t('Custom Route per Departure')}
                  />
                </Grid>
              </Grid>

              {
                values.custom_route_enabled ? (
                  <Box>
                    {values.delivery_timings && values.delivery_timings.length > 0 ? (
                      values.delivery_timings
                        .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
                        .map(timing => renderRouteSetupForTiming(timing))
                    ) : (
                      <Typography variant="body1" color="textSecondary" style={{ marginTop: '16px' }}>
                        {t("Please select Departure Times first to configure the Routes")}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Grid container spacing={2} ml={2}>
                    {user?.permissions?.tours?.extended_features && (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <Select
                            label={t("Tour Type")}
                            name="tour_type"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.tour_type}
                            errors={errors}
                            options={[
                              { label: t("One Way"), value: "one_way" },
                              { label: t("Return to Origin"), value: "return_to_origin" },
                              { label: t("Custom"), value: "custom" },
                            ]}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <Select
                            label={t("Efficient Routing Scheme")}
                            name="route_type"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.route_type}
                            errors={errors}
                            options={[
                              { label: t("Shortest Distance Mapbox"), value: "distances" },
                              { label: t("Shortest Duration Mapbox"), value: "durations" },
                              { label: t("Shortest Distance Google"), value: "distances_google" },
                            ]}
                            required
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <div ref={lastElementRef}>
                        <Select
                          label={(isMixTour ? t("Pickup End / Delivery Start") : t("Tour Start Location")) + " *"}
                          name="supplier_location_id"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.supplier_location_id}
                          errors={errors}
                          options={allowedSupplierLocations.map((o) => ({
                            label: o.name,
                            value: o.id,
                          }))}
                          required
                        />
                      </div>
                    </Grid>
                    {values.tour_type !== "one_way" && user?.permissions?.tours?.extended_features && (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <div ref={lastElementRef}>
                            <Select
                              label={t("Tour End Location") + " *"}
                              name="end_point"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.tour_type === "return_to_origin" ? values.supplier_location_id : values.end_point}
                              errors={errors}
                              options={allowedSupplierLocations.map((o) => ({
                                label: o.name,
                                value: o.id,
                              }))}
                              disabled={values.tour_type === "return_to_origin"}
                              required
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <Checkbox
                            checked={addStopsBeforeEndPoint}
                            value={addStopsBeforeEndPoint}
                            name="add_stops_before_end_point"
                            onChange={() => setAddStopsBeforeEndPoint(!addStopsBeforeEndPoint)}
                            onBlur={handleBlur}
                            errors={errors}
                            style={{ color: "#6F9CEB" }}
                          />
                          <Typography component="span" className="font-size-12">
                            {t("Add Stops Before Finish")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <Checkbox
                            checked={addStopsAfterEndPoint}
                            value={addStopsAfterEndPoint}
                            name="add_stops_after_end_point"
                            onChange={() => setAddStopsAfterEndPoint(!addStopsAfterEndPoint)}
                            onBlur={handleBlur}
                            errors={errors}
                            style={{ color: "#6F9CEB" }}
                          />
                          <Typography component="span" className="font-size-12">
                            {t("Add Stops After Finish")}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )
              }

              {
                !values.custom_route_enabled && values.tour_type !== "one_way" && user?.permissions?.tours?.extended_features && addStopsBeforeEndPoint && (
                  <Grid container spacing={2}>
                    {beforeEndLocations.map((location) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={2}
                        key={location.id}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Select
                          label={t("Stops Before Tour End")}
                          name={`before_end_point_${location.id}`}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[`before_end_point_${location.id}`] || ""}
                          errors={errors}
                          options={allowedSupplierLocations.map((o) => ({
                            label: o.name,
                            value: o.id,
                          }))}
                          required
                          style={{ flex: 1 }}
                        />
                        <IconButton
                          aria-label={t("Remove stop")}
                          onClick={() => handleRemoveBeforeEndLocation(location.id)}
                          style={{ marginLeft: 8 }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={4} lg={2} style={{ display: "flex", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handleAddBeforeEndLocation}
                        style={{
                          marginTop: "4px",
                          backgroundColor: "transparent",
                          borderColor: "#6F9CEB",
                          color: "#6F9CEB",
                          width: "100%",
                          height: "40px",
                        }}
                      >
                        {t("Add Before Finish")}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              {values.tour_type !== "one_way" && user?.permissions?.tours?.extended_features && addStopsAfterEndPoint && (
                <Grid container spacing={2}>
                  {endLocations.map((location) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={2}
                      key={location.id}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Select
                        label={t("Stops After Tour End")}
                        name={`end_point_${location.id}`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values[`end_point_${location.id}`] || ""}
                        errors={errors}
                        options={allowedSupplierLocations.map((o) => ({
                          label: o.name,
                          value: o.id,
                        }))}
                        required
                        style={{ flex: 1 }}
                      />
                      <IconButton
                        aria-label={t("Remove stop")}
                        onClick={() => handleRemoveEndLocation(location.id)}
                        style={{ marginLeft: 8 }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4} lg={2} style={{ display: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={handleAddEndLocation}
                      style={{
                        marginTop: "4px",
                        backgroundColor: "transparent",
                        borderColor: "#6F9CEB",
                        color: "#6F9CEB",
                        width: "100%",
                        height: "40px",
                      }}
                    >
                      {t("Add After Finish")}
                    </Button>
                  </Grid>
                </Grid>
              )}
              <Box mt={8}>
                {user?.permissions?.tours?.extended_features && !values.custom_route_enabled && (
                  <TourRouteVisualization
                    start={supplierLocations.find((o) => o.id === values.supplier_location_id)?.name || "Start Location"}
                    customerStops={values.name ? [values.name] : []}
                    end={
                      values.tour_type === "one_way"
                        ? null
                        : supplierLocations.find(
                          (o) =>
                            o.id ===
                            (values.tour_type === "return_to_origin" ? values.supplier_location_id : values.end_point)
                        )?.name || "End Location"
                    }
                    stopsBeforeEnd={
                      addStopsBeforeEndPoint && values.tour_type !== "one_way"
                        ? beforeEndLocations
                          .map(
                            (location) =>
                              supplierLocations.find((o) => o.id === values[`before_end_point_${location.id}`])?.name || null
                          )
                          .filter(Boolean)
                        : []
                    }
                    stopsAfterEnd={
                      addStopsAfterEndPoint && values.tour_type !== "one_way"
                        ? endLocations
                          .map(
                            (location) =>
                              supplierLocations.find((o) => o.id === values[`end_point_${location.id}`])?.name || null
                          )
                          .filter(Boolean)
                        : []
                    }
                  />
                )}
              </Box>
            </>
          )
        }
      </Box >
      <Dialog
        open={tourTypeDialog.open}
        onClose={closeTourTypeDialog}
        aria-labelledby="tour-type-dialog-title"
        aria-describedby="tour-type-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="tour-type-dialog-title">{t("Confirm")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="tour-type-dialog-description">
            {tourTypeDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTourTypeDialog} color="default">
            {t("Cancel")}
          </Button>
          <Button onClick={confirmTourTypeDialog} color="primary" variant="contained">
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
TourForm.propTypes = {
  initialValues: PropTypes.shape({}),
  handleAddTour: PropTypes.func,
};
export default TourForm;
